import { useState } from "react";
import { MaskLines, Reveal } from "../components/anim";
import { FileDown, Minus, Plus, ArrowUpRight } from "lucide-react";
import { Eyebrow } from "../components/ui-bits";
import PartnerForm from "../components/PartnerForm";
import { PARTNER_AUDIENCES, PARTNER_POINTS } from "../data/site";
import useSEO from "../hooks/useSEO";

function FaqPartnership() {
  const [open, setOpen] = useState(0);
  const faq = [
    {
      q: "Chi paga cosa?",
      a: "Sprizziamoci mette il banco con erogatori alla spina, il prodotto, il personale di servizio, ghiaccio e bicchieri, oltre a montaggio e smontaggio. L'organizzatore mette solo lo spazio a disposizione: al resto ci pensiamo noi.",
    },
    {
      q: "Quanto guadagna il partner socio?",
      a: "Dipende dal pubblico e dal venduto della tua manifestazione: le condizioni economiche e la remunerazione vengono definite insieme e formalizzate nell'accordo commerciale.",
    },
    {
      q: "In quanto tempo si allestisce?",
      a: "Il banco è pensato per essere operativo in giornata: allestiamo e facciamo le prove prima dell'apertura, e alla chiusura smontiamo tutto senza lasciare traccia.",
    },
    {
      q: "C'è un minimo di drink?",
      a: "Nessun minimo rigido: guardiamo insieme il tipo di manifestazione e il pubblico previsto, e definiamo un obiettivo che abbia senso per entrambi.",
    },
    {
      q: "Che spazio serve per il banco?",
      a: "Uno spazio piano e agibile, con passaggio comodo per gli ospiti: la misura esatta e la posizione migliore le valutiamo insieme quando ci racconti il tuo evento.",
    },
  ];
  return (
    <section className="pb-20 md:pb-28">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <p className="eyebrow mb-4">FAQ partnership</p>
          <h3 className="font-display text-3xl md:text-5xl font-semibold mb-12">
            Domande frequenti.
          </h3>
        </Reveal>
        <div className="border-t border-white/[0.07] max-w-4xl">
          {faq.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.05}>
              <div className="border-b border-white/[0.07]">
                <button
                  type="button"
                  data-testid={`partner-faq-toggle-${i}`}
                  aria-expanded={open === i}
                  onClick={() => setOpen((v) => (v === i ? -1 : i))}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                >
                  <span className="font-display text-lg md:text-2xl font-semibold group-hover:text-brand-orange transition-colors duration-200">
                    {f.q}
                  </span>
                  <span className="shrink-0 h-9 w-9 rounded-full border border-white/20 grid place-items-center text-white/60 group-hover:text-brand-orange group-hover:border-brand-orange transition-colors duration-200">
                    {open === i ? <Minus size={15} /> : <Plus size={15} />}
                  </span>
                </button>
                {open === i && (
                  <p className="pb-6 pr-10 text-sm md:text-base leading-relaxed text-white/60 max-w-3xl">
                    {f.a}
                  </p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Partner() {
  useSEO(
    "Partnership beverage per organizzatori | Sprizziamoci",
    "Cocktail alla spina per eventi e servizio bar per sagre e concerti: diventa partner beverage. Noi portiamo banchi, prodotti e personale, tu incassi dai drink venduti."
  );
  return (
    <article data-testid="partner-page">
      <section className="pt-32 md:pt-44 pb-14">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Eyebrow>Partner professionali</Eyebrow>
          <MaskLines
            lines={["SE ORGANIZZI EVENTI,", "DIVENTIAMO PARTNER."]}
            as="h1"
            when="load"
            delay={0.15}
            lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.6rem,6.5vw,5.5rem)]"
            className="mt-6"
          />
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-2.5 max-w-3xl">
              {PARTNER_AUDIENCES.map((a) => (
                <span
                  key={a}
                  className="rounded-full border border-white/15 px-4 py-2 text-xs text-white/65"
                >
                  {a}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-base md:text-lg leading-relaxed text-white/70">
                Postazione, impianti e, secondo la formula concordata, prodotti, consumabili e
                personale. Completiamo il lavoro di chi organizza: un servizio specializzato da
                integrare nella tua proposta.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-brand-orange/30 bg-brand-orange/[0.05] p-8">
                <p className="eyebrow mb-4">Il nostro valore</p>
                <p className="font-display text-2xl md:text-3xl leading-snug">
                  I tuoi ospiti si servono da soli, senza attese. Noi teniamo la postazione
                  perfetta, dal primo all'ultimo brindisi.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <h3 className="font-display text-3xl md:text-5xl font-semibold mb-12">
              Cosa mettiamo in gioco.
            </h3>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PARTNER_POINTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06} className={i === 0 ? "lg:col-span-2" : ""}>
                <div
                  className={`group rounded-3xl border border-white/[0.07] bg-brand-surface/30 p-8 h-full transition-colors duration-300 hover:border-brand-orange/40 ${
                    i === 0 ? "bg-brand-orange/[0.06]" : ""
                  }`}
                >
                  <p className="font-display text-2xl font-semibold group-hover:text-brand-orange transition-colors duration-300">
                    {p.title}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <div className="rounded-3xl border border-brand-orange/30 bg-brand-orange/[0.05] p-8 md:p-12">
              <p className="eyebrow mb-4">Modello partnership</p>
              <h3 className="font-display text-3xl md:text-4xl font-semibold leading-tight">
                Diventa partner socio.
              </h3>
              <p className="mt-5 text-sm md:text-base leading-relaxed text-white/70 max-w-3xl">
                Sei un wedding planner o un organizzatore di eventi? Diventiamo partner:
                Sprizziamoci mette le attrezzature (banco con erogatori alla spina), il prodotto
                e il personale di servizio; l'organizzazione mette solo lo spazio a disposizione
                e diventa partner socio, partecipando ai ricavi su ogni drink venduto.
              </p>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-white/70 max-w-3xl">
                Il nostro sistema operativo nasce per la gestione dei grandi numeri: permette alle
                organizzazioni di offrire un servizio beverage professionale senza investire in
                prodotto e risorse umane.
              </p>
              <div className="mt-8 grid gap-6 md:grid-cols-2">
                <div>
                  <p className="eyebrow mb-2">Sprizziamoci</p>
                  <p className="text-sm leading-relaxed text-white/65">
                    Banco self service, erogatori alla spina, prodotto, personale di servizio,
                    ghiaccio e bicchieri: montaggio, assistenza e smontaggio inclusi. Al resto
                    ci pensiamo noi.
                  </p>
                </div>
                <div>
                  <p className="eyebrow mb-2">L'organizzatore</p>
                  <p className="text-sm leading-relaxed text-white/65">
                    Solo lo spazio a disposizione per il banco, dentro la tua manifestazione o
                    il tuo evento.
                  </p>
                </div>
              </div>
              <div className="mt-8 rounded-2xl border border-brand-orange/40 bg-brand-ink p-6 md:p-8">
                <p className="font-display text-2xl md:text-3xl font-semibold text-white">
                  Vuoi informazioni sulla partnership?
                </p>
                <p className="mt-3 text-sm leading-relaxed text-white/65 max-w-xl">
                  Raccontaci la tua manifestazione o il tuo servizio: ti ricontattiamo con la
                  formula giusta. Le condizioni economiche e la remunerazione vengono definite
                  insieme e formalizzate nell'accordo commerciale.
                </p>
                <a
                  href="#partner-form"
                  data-testid="partner-info-cta"
                  className="mt-6 inline-flex items-center gap-3 rounded-full bg-brand-orange px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink hover:bg-[#ff9433] transition-colors duration-300"
                >
                  Richiedi informazioni <ArrowUpRight size={15} />
                </a>
              </div>
              <p className="mt-6 text-[11px] leading-relaxed text-white/40">
                Ogni collaborazione viene valutata a discrezione dell'organizzazione Sprizziamoci,
                in base allo spazio, al pubblico e al tipo di manifestazione.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqPartnership />

      <section id="partner-form" className="pb-24 md:pb-32 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <div className="grid gap-14 lg:grid-cols-12 mb-14">
              <div className="lg:col-span-6">
                <p className="eyebrow mb-4">Modulo dedicato ai professionisti</p>
                <h3 className="font-display text-3xl md:text-5xl font-semibold leading-tight">
                  Raccontaci i tuoi eventi: costruiamo la formula giusta.
                </h3>
                <p className="mt-6 text-sm leading-relaxed text-white/55 max-w-md">
                  Preventivi per operatori e collaborazioni ricorrenti. Per i clienti privati
                  c'è la pagina{" "}
                  <a href="/contatti" className="text-brand-orange underline underline-offset-2">
                    contatti e preventivi
                  </a>
                  .
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href="/brochure-partner-socio.pdf"
                    download="Brochure-partner-socio-Sprizziamoci.pdf"
                    data-testid="partner-download-brochure"
                    className="inline-flex items-center gap-2.5 rounded-full bg-brand-orange px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-ink hover:bg-[#ff9433] transition-colors duration-300"
                  >
                    <FileDown size={15} /> Brochure partner socio (PDF)
                  </a>
                  <a
                    href="/modello-accordo-partnership.pdf"
                    download="Modello-accordo-partnership-Sprizziamoci.pdf"
                    data-testid="partner-download-contratto"
                    className="inline-flex items-center gap-2.5 rounded-full border border-brand-orange/50 px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-orange hover:bg-brand-orange hover:text-brand-ink transition-colors duration-300"
                  >
                    <FileDown size={15} /> Modello di accordo (PDF)
                  </a>
                  <a
                    href="/scheda-tecnica-sprizziamoci.pdf"
                    download="Scheda-tecnica-Sprizziamoci.pdf"
                    data-testid="partner-download-scheda"
                    className="inline-flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:border-brand-orange hover:text-brand-orange transition-colors duration-300"
                  >
                    <FileDown size={15} /> Scheda tecnica del banco (PDF)
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
          <PartnerForm />
        </div>
      </section>
    </article>
  );
}
